import { useMemo, useState } from 'react'
import { useQuery, useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute, Link, notFound, useNavigate } from '@tanstack/react-router'
import type { ColumnDef } from '@tanstack/react-table'
import {
  AlertCircleIcon,
  DownloadIcon,
  FileTextIcon,
  MoreHorizontalIcon,
  PlusIcon,
  ReceiptIcon,
  Trash2Icon,
  UsersIcon,
} from 'lucide-react'

import { ApiClientError } from '@ems/api-client/client'
import { Alert, AlertDescription, AlertTitle } from '@ems/ui/components/alert'
import { Button } from '@ems/ui/components/button'
import { Card, CardContent } from '@ems/ui/components/card'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@ems/ui/components/dropdown-menu'
import { Input } from '@ems/ui/components/input'
import { ConfirmDialog } from '@ems/ui/patterns/confirm-dialog'
import { DataTable } from '@ems/ui/patterns/data-table'
import { EmptyState } from '@ems/ui/patterns/empty-state'
import { Field } from '@ems/ui/patterns/field'
import { PageHeader } from '@ems/ui/patterns/page-header'
import { StatusBadge, type StatusTone } from '@ems/ui/patterns/status-badge'
import { formatDateTime, formatMoney } from '@ems/ui/lib/format'

import { NotFoundView } from '#admin/components/not-found-view.tsx'
import { ReadOnlyNotice } from '#admin/components/read-only-notice.tsx'
import { sessionQuery } from '#admin/features/auth/queries.ts'
import { can } from '#admin/features/auth/session.ts'
import {
  availableActions,
  NO_ACTIONS,
  awaitingAction,
  fetchPaymentProofLink,
  issuesOf,
  label,
  paymentTone,
  registrationTone,
  verificationTone,
  type AvailableActions,
  type EmailKind,
  type FieldIssue,
  type Participant,
  type Registration,
} from '#admin/features/registrations/api.ts'
import { ConfirmPaymentDialog } from '#admin/features/registrations/confirm-payment-dialog.tsx'
import { ContactDialog } from '#admin/features/registrations/contact-dialog.tsx'
import { RegistrationDecisionDialogs } from '#admin/features/registrations/decision-dialogs.tsx'
import { FormDialog, NoteDialog } from '#admin/features/registrations/form-dialog.tsx'
import { HistoryCard } from '#admin/features/registrations/history-card.tsx'
import {
  useApplyVoucher,
  useConfirmPayment,
  useReopenVerification,
  useDeleteParticipant,
  useDeleteRegistration,
  useReinvoiceRegistration,
  useRemoveVoucher,
  useResendEmail,
  useSubmitRegistration,
  useUpdateContact,
} from '#admin/features/registrations/mutations.ts'
import { ParticipantSheet } from '#admin/features/registrations/participant-sheet.tsx'
import { SubmitDialog } from '#admin/features/registrations/submit-dialog.tsx'
import { registrationQuery } from '#admin/features/registrations/queries.ts'
import { asRegistrationForm } from '#admin/features/registration-form/api.ts'
import { formSettingsQuery } from '#admin/features/registration-form/queries.ts'
import { ticketTypesQuery } from '#admin/features/ticket-types/queries.ts'
import { moduleAccess } from '#admin/features/tenant/api.ts'
import { manifestQuery } from '#admin/features/tenant/queries.ts'
import { StandBanner } from '#admin/features/exhibitor/stand-banner.tsx'
import { openSignedLink } from '#admin/lib/open-signed-link.ts'
import { RegistrationSkeleton } from '#admin/features/registrations/registration-skeletons.tsx'
import { TicketsSection } from '#admin/features/registrations/tickets-section.tsx'

/**
 * `/ticketing/registrations/$registrationId` — one registration, and every decision an operator makes on it.
 *
 * **Each verb is offered only when the registration's axes allow it** (`availableActions`). The
 * server refuses every one of them again; this keeps the page from offering what it would refuse.
 */
export const Route = createFileRoute('/_authenticated/ticketing/registrations/$registrationId/')({
  pendingComponent: RegistrationSkeleton,
  loader: async ({ context, params }) => {
    try {
      await Promise.all([
        context.queryClient.ensureQueryData(registrationQuery(params.registrationId)),
        context.queryClient.ensureQueryData(ticketTypesQuery()),
        context.queryClient.ensureQueryData(formSettingsQuery()),
      ])
    } catch (error) {
      // A registration that does not exist (or was deleted) is "not here", not a failure. A
      // module missing from the plan is also a 404, and that one ErrorView explains properly.
      if (error instanceof ApiClientError && error.status === 404 && !error.isMissingFeature) {
        throw notFound()
      }
      throw error
    }
  },
  notFoundComponent: NotFoundView,
  component: RegistrationPage,
})

type Dialog =
  | { kind: 'submit' }
  | { kind: 'confirm-payment' }
  | { kind: 'approve' }
  | { kind: 'reject' }
  | { kind: 'reopen' }
  | { kind: 'cancel' }
  | { kind: 'delete' }
  | { kind: 'voucher' }
  | { kind: 'contact' }
  | { kind: 'reinvoice' }
  | { kind: 'participant'; participant: Participant; errors?: Record<string, string> }
  | { kind: 'remove-participant'; participant: Participant }
  | null

function RegistrationPage() {
  const { registrationId: id } = Route.useParams()
  const { data: registration } = useSuspenseQuery(registrationQuery(id))
  const navigate = useNavigate()
  const [dialog, setDialog] = useState<Dialog>(null)
  const close = () => setDialog(null)
  const onOpenChange = (open: boolean) => (open ? undefined : close())

  const submit = useSubmitRegistration()
  const confirm = useConfirmPayment()
  const reopen = useReopenVerification()
  const remove = useDeleteRegistration(() => navigate({ to: '/ticketing/registrations' }))
  const voucher = useApplyVoucher()
  const removeVoucher = useRemoveVoucher()
  const contact = useUpdateContact()
  const reinvoice = useReinvoiceRegistration()
  const resend = useResendEmail()
  const removeParticipant = useDeleteParticipant(id)
  const { data: settings } = useSuspenseQuery(formSettingsQuery())
  const { data: types } = useSuspenseQuery(ticketTypesQuery())
  const { data: manifest } = useQuery(manifestQuery())
  const form = asRegistrationForm(settings)
  /** What a refused submit said, participant by participant, until the next attempt. */
  const [issues, setIssues] = useState<FieldIssue[]>([])

  const { data: session } = useSuspenseQuery(sessionQuery())
  // View-only roles see the registration and none of its verbs; the server refuses them anyway.
  const canManage = can(session, 'ticket.manage')
  const billingFull = moduleAccess(manifest ?? undefined, 'billing') === 'full'
  const offered = canManage ? availableActions(registration) : NO_ACTIONS
  // A new invoice needs billing `full`, as on the server.
  const actions = { ...offered, reinvoice: offered.reinvoice && billingFull }
  const reference = registration.code ?? 'this draft'

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={
          registration.code === null ? (
            'Draft registration'
          ) : (
            <span className="font-mono">{registration.code}</span>
          )
        }
        description={[
          registration.contactName,
          registration.contactEmail,
          registration.contactPhone,
        ]
          .filter(Boolean)
          .join(' · ')}
        actions={
          <>
            {actions.submit ? (
              <Button onClick={() => setDialog({ kind: 'submit' })}>Submit</Button>
            ) : null}
            {actions.confirmPayment === null ? null : (
              <Button onClick={() => setDialog({ kind: 'confirm-payment' })}>
                {actions.confirmPayment === 'confirm' ? 'Confirm payment' : 'Confirm registration'}
              </Button>
            )}
            {actions.decideVerification ? (
              <>
                <Button variant="outline" onClick={() => setDialog({ kind: 'approve' })}>
                  Approve
                </Button>
                <Button variant="outline" onClick={() => setDialog({ kind: 'reject' })}>
                  Reject
                </Button>
              </>
            ) : null}
            {actions.reopenVerification ? (
              <Button variant="outline" onClick={() => setDialog({ kind: 'reopen' })}>
                Reopen review
              </Button>
            ) : null}
            {/* A plain link, as the invoice PDF is: the browser shows the download. */}
            <Button asChild variant="outline">
              <a href={`/v1/ticket/admin/registrations/${id}/pdf`} download>
                <DownloadIcon />
                PDF
              </a>
            </Button>
            {canManage ? (
              <MoreActions
                actions={actions}
                open={(kind) => setDialog({ kind })}
                resend={(kind) => resend.mutate({ id, kind })}
                resending={resend.isPending}
              />
            ) : null}
          </>
        }
      />

      {canManage ? null : <ReadOnlyNotice area="this registration" />}

      <StandBanner registrationId={registration.id} />

      {registration.canceledAt === null ? null : (
        <Alert variant="destructive">
          <AlertCircleIcon />
          <AlertTitle>Canceled {formatDateTime(registration.canceledAt)}</AlertTitle>
          {registration.cancelReason === null ? null : (
            <AlertDescription>{registration.cancelReason}</AlertDescription>
          )}
          {/* A canceled registration with an invoice may need a credit note or a void there. */}
          {registration.invoiceId === null ? null : (
            <AlertDescription className="mt-2 flex flex-wrap items-center gap-3">
              <span>Check the linked invoice: a payment may need a credit note.</span>
              <Button asChild variant="outline" size="sm">
                <Link
                  to="/billing/invoices/$invoiceId"
                  params={{ invoiceId: registration.invoiceId }}
                >
                  <ReceiptIcon />
                  View linked invoice
                </Link>
              </Button>
            </AlertDescription>
          )}
        </Alert>
      )}
      <AxesSection registration={registration} />
      <SubmitIssues
        issues={issues}
        registration={registration}
        onFix={(participant, errors) => setDialog({ kind: 'participant', participant, errors })}
      />
      <ParticipantsSection
        registration={registration}
        canEdit={actions.editParticipants}
        canAdd={actions.addParticipant}
        // A registration keeps one participant at least; the last one goes with the registration.
        canRemove={actions.addParticipant && registration.participants.length > 1}
        open={setDialog}
        onResendTicket={
          actions.resendTickets
            ? (participantId) => resend.mutate({ id, kind: 'ticket_issued', participantId })
            : undefined
        }
        resending={resend.isPending}
      />
      {registration.completedAt === null ? null : <TicketsSection registrationId={id} />}
      <div className="grid gap-6 md:grid-cols-2">
        <MoneySection
          registration={registration}
          {...(actions.removeVoucher
            ? { onRemoveVoucher: () => removeVoucher.mutate(id), removing: removeVoucher.isPending }
            : {})}
        />
        <PaperworkSection registration={registration} />
      </div>
      <HistoryCard registrationId={id} />

      {/* ---- Dialogs. One at a time; each closes when its mutation settles. ---- */}

      <SubmitDialog
        open={dialog?.kind === 'submit'}
        onOpenChange={onOpenChange}
        reference={reference}
        // The same test as the server: an invoice only when billing is full and it costs money.
        invoiced={billingFull && registration.total > 0}
        contact={{ name: registration.contactName, email: registration.contactEmail }}
        busy={submit.isPending}
        onSubmit={(body) =>
          submit.mutate(
            { id, body },
            {
              onSuccess: () => {
                setIssues([])
                close()
              },
              // Field issues are listed on the page, each with a way to fix it; the dialog closes
              // so the list can be read. The toast still says the submit was refused.
              onError: (error) => {
                setIssues(issuesOf(error))
                close()
              },
            },
          )
        }
      />

      <ConfirmPaymentDialog
        open={dialog?.kind === 'confirm-payment' && actions.confirmPayment === 'confirm'}
        onOpenChange={onOpenChange}
        reference={reference}
        total={registration.total}
        currency={registration.currency}
        // The same test as the server: billing takes the payment when it is full and invoiced.
        invoiced={billingFull && registration.invoiceId !== null}
        canUpload={can(session, 'identity.write')}
        zone={manifest?.tenant.timezone ?? 'UTC'}
        busy={confirm.isPending}
        onConfirm={(body) => confirm.mutate({ id, body }, { onSuccess: close })}
      />

      <ConfirmDialog
        open={dialog?.kind === 'confirm-payment' && actions.confirmPayment === 'complete'}
        onOpenChange={onOpenChange}
        title={`Confirm registration ${reference}?`}
        description={`Tickets are issued for all ${registration.participants.length} participant(s) and their QR codes generated.`}
        confirmLabel="Confirm registration"
        busy={confirm.isPending}
        onConfirm={() => confirm.mutate({ id }, { onSuccess: close })}
      />

      <RegistrationDecisionDialogs
        registration={registration}
        dialog={
          dialog?.kind === 'approve' || dialog?.kind === 'reject' || dialog?.kind === 'cancel'
            ? dialog.kind
            : null
        }
        onClose={close}
      />

      <ConfirmDialog
        open={dialog?.kind === 'reopen'}
        onOpenChange={onOpenChange}
        title={`Reopen review for ${reference}?`}
        description="Verification goes back to pending so it can be approved or rejected again. The rejection reason stays in the audit log."
        confirmLabel="Reopen review"
        busy={reopen.isPending}
        onConfirm={() => reopen.mutate(id, { onSuccess: close })}
      />

      <NoteDialog
        open={dialog?.kind === 'reinvoice'}
        onOpenChange={onOpenChange}
        title={`Create a new invoice for ${reference}?`}
        description="Billing voids the current invoice, if it is not void already. Then it issues a new invoice from the participants as they are now. The billing name, email and payment method do not change."
        confirmLabel="Create new invoice"
        noteLabel="Reason"
        placeholder="e.g. The participant changed after the invoice was issued."
        required
        busy={reinvoice.isPending}
        onConfirm={(reason) => reinvoice.mutate({ id, reason }, { onSuccess: close })}
      />

      <ConfirmDialog
        open={dialog?.kind === 'delete'}
        onOpenChange={onOpenChange}
        title={`Delete ${reference}?`}
        description="It disappears from every list and export. A registration with live tickets has to be canceled first."
        confirmLabel="Delete"
        destructive
        busy={remove.isPending}
        onConfirm={() => remove.mutate(id, { onSettled: close })}
      />

      <ConfirmDialog
        open={dialog?.kind === 'remove-participant'}
        onOpenChange={onOpenChange}
        title={
          dialog?.kind === 'remove-participant'
            ? `Remove ${dialog.participant.fullName} from this registration?`
            : ''
        }
        description="The draft will be repriced."
        confirmLabel="Remove"
        destructive
        busy={removeParticipant.isPending}
        onConfirm={() => {
          if (dialog?.kind !== 'remove-participant') return
          removeParticipant.mutate(dialog.participant.id, { onSuccess: close })
        }}
      />

      <VoucherDialog
        open={dialog?.kind === 'voucher'}
        onOpenChange={onOpenChange}
        busy={voucher.isPending}
        onConfirm={(code) => voucher.mutate({ id, code }, { onSuccess: close })}
      />

      {dialog?.kind === 'contact' ? (
        <ContactDialog
          registration={registration}
          defaultCountry={form.defaultCountry}
          busy={contact.isPending}
          onClose={close}
          onConfirm={(input) => contact.mutate({ id, input }, { onSuccess: close })}
        />
      ) : null}

      {dialog?.kind === 'participant' ? (
        <ParticipantSheet
          // Keyed so each participant opens with their own values, not the last one's edits.
          key={dialog.participant.id}
          registrationId={id}
          participant={dialog.participant}
          ticketTypeName={
            types.items.find((type) => type.id === dialog.participant.ticketTypeId)?.name ??
            'Unknown type'
          }
          form={form}
          canManage={canManage}
          canEdit={actions.editParticipants}
          canUpload={can(session, 'identity.write')}
          {...(dialog.errors === undefined ? {} : { initialErrors: dialog.errors })}
          onClose={close}
        />
      ) : null}
    </div>
  )
}

/**
 * The verbs that do not move the registration forward, behind one button — v1's layout. The
 * forward verbs stay as buttons in the header, where the next step is one click.
 */
function MoreActions({
  actions,
  open,
  resend,
  resending,
}: {
  actions: AvailableActions
  open: (kind: 'voucher' | 'contact' | 'reinvoice' | 'cancel' | 'delete') => void
  resend: (kind: EmailKind) => void
  /** True while an email is on its way. A second click must not send it twice. */
  resending: boolean
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline">
          <MoreHorizontalIcon />
          More actions
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {actions.applyVoucher ? (
          <DropdownMenuItem onSelect={() => open('voucher')}>Apply voucher</DropdownMenuItem>
        ) : null}
        {actions.editContact ? (
          <DropdownMenuItem onSelect={() => open('contact')}>Edit contact</DropdownMenuItem>
        ) : null}
        {actions.reinvoice ? (
          <DropdownMenuItem onSelect={() => open('reinvoice')}>Create new invoice</DropdownMenuItem>
        ) : null}
        {actions.resendTickets ? (
          <>
            <DropdownMenuItem disabled={resending} onSelect={() => resend('payment_completed')}>
              Resend payment confirmation
            </DropdownMenuItem>
            <DropdownMenuItem disabled={resending} onSelect={() => resend('ticket_issued')}>
              Resend tickets to participants
            </DropdownMenuItem>
          </>
        ) : null}
        {actions.resendApproval ? (
          <DropdownMenuItem disabled={resending} onSelect={() => resend('registration_pending')}>
            Resend approval request to staff
          </DropdownMenuItem>
        ) : null}
        {actions.applyVoucher ||
        actions.editContact ||
        actions.reinvoice ||
        actions.resendTickets ||
        actions.resendApproval ? (
          <DropdownMenuSeparator />
        ) : null}
        {actions.cancel ? (
          <DropdownMenuItem variant="destructive" onSelect={() => open('cancel')}>
            Cancel registration
          </DropdownMenuItem>
        ) : null}
        <DropdownMenuItem variant="destructive" onSelect={() => open('delete')}>
          Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

/**
 * The three axes as three badges, and the next step derived from all of them.
 *
 * Never collapsed into one (invariant 06): "paid, awaiting approval" is the combination that
 * matters, and a single badge would have to pick a side on it.
 */
function AxesSection({ registration }: { registration: Registration }) {
  const next = awaitingAction(registration)
  return (
    <section className="flex flex-col gap-3">
      <div className="grid grid-cols-1 gap-3 min-[400px]:grid-cols-3">
        <Axis
          name="Registration"
          value={label(registration.status)}
          tone={registrationTone(registration.status)}
        />
        <Axis
          name="Payment"
          value={label(registration.paymentStatus)}
          tone={paymentTone(registration.paymentStatus)}
        />
        <Axis
          name="Verification"
          value={label(registration.verificationStatus)}
          tone={verificationTone(registration.verificationStatus)}
          note={registration.verificationNote}
        />
      </div>
      {next === null ? null : (
        <Alert>
          <AlertDescription>{next}</AlertDescription>
        </Alert>
      )}
    </section>
  )
}

/**
 * A refused submit, participant by participant: what each is missing, and a button that opens
 * their details on the form with the messages beside the fields.
 */
function SubmitIssues({
  issues,
  registration,
  onFix,
}: {
  issues: FieldIssue[]
  registration: Registration
  onFix: (participant: Participant, errors: Record<string, string>) => void
}) {
  if (issues.length === 0) return null
  const byParticipant = registration.participants
    .map((participant) => ({
      participant,
      found: issues.filter((issue) => issue.participantId === participant.id),
    }))
    .filter((entry) => entry.found.length > 0)
  const other = issues.filter((issue) => issue.participantId === null)

  return (
    <Alert variant="destructive">
      <AlertCircleIcon />
      <AlertTitle>
        {byParticipant.length === 1
          ? '1 participant needs attention before this can be submitted'
          : `${byParticipant.length} participants need attention before this can be submitted`}
      </AlertTitle>
      <AlertDescription>
        <ul className="mt-2 flex flex-col gap-3">
          {byParticipant.map(({ participant, found }) => (
            <li
              key={participant.id}
              className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between"
            >
              <div className="flex flex-col">
                <span className="font-medium">{participant.fullName}</span>
                <span>{found.map((issue) => issue.message).join(' ')}</span>
              </div>
              <Button
                size="sm"
                variant="outline"
                className="self-start"
                onClick={() =>
                  onFix(
                    participant,
                    Object.fromEntries(found.map((issue) => [issue.field, issue.message])),
                  )
                }
              >
                Fix
              </Button>
            </li>
          ))}
          {other.map((issue) => (
            <li key={`${issue.field}-${issue.message}`}>{issue.message}</li>
          ))}
        </ul>
      </AlertDescription>
    </Alert>
  )
}

function Axis({
  name,
  value,
  tone,
  note = null,
}: {
  name: string
  value: string
  tone: StatusTone
  note?: string | null
}) {
  return (
    <Card>
      <CardContent className="flex flex-col items-start gap-2 p-3">
        <p className="text-muted-foreground text-xs">{name}</p>
        <StatusBadge tone={tone}>{value}</StatusBadge>
        {note === null ? null : <p className="text-muted-foreground text-xs">{note}</p>}
      </CardContent>
    </Card>
  )
}

function ParticipantsSection({
  registration,
  canEdit,
  canAdd,
  canRemove,
  open,
  onResendTicket,
  resending,
}: {
  registration: Registration
  canEdit: boolean
  canAdd: boolean
  canRemove: boolean
  open: (dialog: Dialog) => void
  /** Present only when the registration has tickets to send. */
  onResendTicket?: ((participantId: string) => void) | undefined
  /** True while an email is on its way. A second click must not send it twice. */
  resending: boolean
}) {
  const { data: types } = useSuspenseQuery(ticketTypesQuery())
  const typeName = useMemo(
    () => new Map(types.items.map((type) => [type.id, type.name])),
    [types.items],
  )

  const columns = useMemo<ColumnDef<Participant, never>[]>(
    () => [
      {
        accessorKey: 'fullName',
        header: 'Name',
        meta: { primary: true },
        cell: ({ row }) => <span className="font-medium">{row.original.fullName}</span>,
      },
      { accessorKey: 'email', header: 'Email' },
      {
        id: 'ticketType',
        header: 'Ticket type',
        cell: ({ row }) => typeName.get(row.original.ticketTypeId) ?? 'Unknown type',
      },
      {
        id: 'badgeName',
        header: 'Badge name',
        cell: ({ row }) => row.original.badgeName ?? '—',
      },
      {
        id: 'company',
        header: 'Company',
        cell: ({ row }) => row.original.companyName ?? '—',
      },
      {
        id: 'lineTotal',
        header: 'Line total',
        meta: { align: 'end' },
        cell: ({ row }) => formatMoney(row.original.lineTotal, registration.currency),
      },
      {
        id: 'actions',
        header: () => <span className="sr-only">Actions</span>,
        meta: { align: 'end' },
        // Always offered: the details (answers, files) are read here even by a view-only role;
        // editing is inside, for those who may.
        cell: ({ row }) => (
          <>
            {onResendTicket === undefined ? null : (
              <Button
                variant="ghost"
                size="sm"
                disabled={resending}
                onClick={() => onResendTicket(row.original.id)}
              >
                Resend ticket
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => open({ kind: 'participant', participant: row.original })}
            >
              {canEdit ? 'Details' : 'View'}
            </Button>
            {canRemove ? (
              <Button
                variant="ghost"
                size="sm"
                aria-label={`Remove ${row.original.fullName}`}
                onClick={() => open({ kind: 'remove-participant', participant: row.original })}
              >
                <Trash2Icon />
              </Button>
            ) : null}
          </>
        ),
      },
    ],
    [typeName, registration.currency, canEdit, canRemove, open, onResendTicket, resending],
  )

  return (
    <DataTable
      caption="Participants in this registration"
      title="Participants"
      description={`${registration.participants.length} in this registration`}
      actions={
        canAdd ? (
          <Button asChild variant="outline">
            <Link
              to="/ticketing/registrations/$registrationId/add-participants"
              params={{ registrationId: registration.id }}
            >
              <PlusIcon />
              Add participants
            </Link>
          </Button>
        ) : null
      }
      columns={columns}
      data={registration.participants}
      getRowId={(p) => p.id}
      empty={
        <EmptyState
          icon={<UsersIcon />}
          title="No participants yet"
          description="A draft needs at least one before it can be submitted."
        />
      }
    />
  )
}

/** Formatting only — every figure was computed by the server (invariant 05). */
function MoneySection({
  registration,
  onRemoveVoucher,
  removing = false,
}: {
  registration: Registration
  /** Present only while the voucher can come off — a draft that has one. */
  onRemoveVoucher?: () => void
  removing?: boolean
}) {
  const money = (amount: number) => formatMoney(amount, registration.currency)
  const remove =
    onRemoveVoucher === undefined ? null : (
      <Button
        variant="link"
        size="sm"
        className="h-auto p-0 text-xs"
        disabled={removing}
        onClick={onRemoveVoucher}
      >
        {removing ? 'Removing…' : 'Remove'}
      </Button>
    )
  return (
    <section className="bg-card flex flex-col gap-3 rounded-lg border border-(--border) p-4 md:p-6">
      <h2 className="text-base font-semibold">Pricing</h2>
      <dl className="grid grid-cols-[1fr_auto] gap-x-4 gap-y-2 text-sm tabular-nums">
        <dt className="text-muted-foreground">Subtotal</dt>
        <dd className="text-right">{money(registration.subtotal)}</dd>
        {registration.discountTotal === 0 ? null : (
          <>
            <dt className="text-muted-foreground flex items-center gap-2">
              Discount{registration.voucherCode === null ? '' : ` (${registration.voucherCode})`}
              {remove}
            </dt>
            <dd className="text-right">−{money(registration.discountTotal)}</dd>
          </>
        )}
        <dt className="text-muted-foreground">Tax</dt>
        <dd className="text-right">{money(registration.taxTotal)}</dd>
        <dt className="font-medium">Total</dt>
        <dd className="text-right font-medium">{money(registration.total)}</dd>
      </dl>
      {registration.voucherCode !== null && registration.discountTotal === 0 ? (
        <p className="text-muted-foreground text-xs">
          Voucher {registration.voucherCode} applied, with no discount on these ticket types.{' '}
          {remove}
        </p>
      ) : null}
    </section>
  )
}

function PaperworkSection({ registration }: { registration: Registration }) {
  const [opening, setOpening] = useState(false)

  const openProof = async () => {
    setOpening(true)
    await openSignedLink(() => fetchPaymentProofLink(registration.id))
    setOpening(false)
  }

  return (
    <section className="bg-card flex flex-col gap-3 rounded-lg border border-(--border) p-4 md:p-6">
      <h2 className="text-base font-semibold">Paperwork</h2>
      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
        <dt className="text-muted-foreground">Submitted</dt>
        <dd>
          {registration.submittedAt === null ? 'Not yet' : formatDateTime(registration.submittedAt)}
        </dd>
        <dt className="text-muted-foreground">Completed</dt>
        <dd>
          {registration.completedAt === null ? 'Not yet' : formatDateTime(registration.completedAt)}
        </dd>
        <dt className="text-muted-foreground">Invoice</dt>
        <dd>
          {registration.invoiceId === null ? (
            'None'
          ) : (
            <Link to="/billing/invoices" className="hover:underline">
              In billing
            </Link>
          )}
        </dd>
      </dl>
      {registration.paymentProofKey === null ? (
        <p className="text-muted-foreground text-sm">No proof of transfer attached.</p>
      ) : (
        <Button
          variant="outline"
          className="self-start"
          disabled={opening}
          onClick={() => void openProof()}
        >
          <FileTextIcon />
          {opening ? 'Opening…' : 'View proof of transfer'}
        </Button>
      )}
    </section>
  )
}

function VoucherDialog({
  open,
  onOpenChange,
  busy,
  onConfirm,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  busy: boolean
  onConfirm: (code: string) => void
}) {
  const [code, setCode] = useState('')
  return (
    <FormDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) setCode('')
        onOpenChange(next)
      }}
      title="Apply a voucher"
      description="The registration is repriced from its participants. Applying another replaces this one rather than stacking."
      confirmLabel="Apply"
      busy={busy}
      canSubmit={code.trim() !== ''}
      onSubmit={() => onConfirm(code.trim())}
    >
      <Field label="Voucher code" required>
        {(props) => (
          <Input
            {...props}
            value={code}
            maxLength={60}
            autoComplete="off"
            onChange={(event) => setCode(event.target.value)}
          />
        )}
      </Field>
    </FormDialog>
  )
}
